import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { INSTANT_SEARCH_INDEX_NAME, INSTANT_SEARCH_PRESET, INSTANT_SEARCH_QUERY_SUGGESTIONS, INSTANT_SEARCH_SEARCH_URL } from '../config/constants';
import { searchClient, typesenseClient } from '../config/searchClient';
import { manufacturerIcons } from '../config/icons';
import { debounce } from 'instantsearch.js/es/lib/utils/debounce';

Vue.component('kk-autocomplete-search-box', {

  template: "#kk-autocomplete-search-box",
  data () {
    return {
      autocompleteInstance: null,
    };
  },
  methods: {
    setInstantSearchUiState ({ query, refinementList, hierarchicalMenu }, reset = false) {
      if (this.instantSearchInstance && window.location.href.includes(INSTANT_SEARCH_SEARCH_URL)) {
        this.instantSearchInstance.setUiState((uiState) => ({
          ...uiState,
          [INSTANT_SEARCH_INDEX_NAME]: {
            ...(reset ? uiState[INSTANT_SEARCH_INDEX_NAME] : {}),
            page: 1,
            query,
            refinementList: {
              ...(reset ? {} : uiState[INSTANT_SEARCH_INDEX_NAME]?.refinementList),
              ...refinementList,
            },
            hierarchicalMenu: {
              ...(reset ? {} : uiState[INSTANT_SEARCH_INDEX_NAME]?.hierarchicalMenu),
              ...hierarchicalMenu,
            },
          },
        }));
      } else if (reset) {
        window.location.href = `${INSTANT_SEARCH_SEARCH_URL}?query=${query}`;
      }
    },
    registerInstantSearchInstance (instantSearchInstance) {
      this.instantSearchInstance = instantSearchInstance;
      instantSearchInstance.on('render', () => {
        const state = instantSearchInstance.mainIndex.getHelper()?.state || {};
        this.autocompleteInstance?.setContext({ query: state.query })
      });
    }
  },
  mounted () {
    const { setInstantSearchUiState } = this;
    const debouncedInstantSearchUiState = debounce(setInstantSearchUiState, 300);

    // Create the Recent Searches plugin
    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
      key: 'recentSearchesPlugin',
      limit: 3,
      transformSource ({ source }) {
        return {
          ...source,
          onSelect ({ item }) {
            $("#search-bar-collapse").collapse('hide');
            setInstantSearchUiState({ query: item.label }, true);
          },
        };
      },
    });

    // Create the Query Suggestions plugin
    const querySuggestionsPlugin = createQuerySuggestionsPlugin({
      searchClient,
      indexName: INSTANT_SEARCH_QUERY_SUGGESTIONS,
      getSearchParams ({ state }) {
        return recentSearchesPlugin.data.getAlgoliaSearchParams({
          query: state.query,
          hitsPerPage: 6,
        });
      },
      transformSource ({ source }) {
        return {
          ...source,
          sourceId: 'querySuggestionsPlugin',
          getItems (params) {
            // We don't display Query Suggestions when there's no query
            if (!params.state.query) return [];

            const { transformResponse, ...result } = source.getItems(params);
            result.transformResponse = (response) => {
              return transformResponse(response).map(({ q, count }) => ({
                query: q,
                popularity: count
              }));
            };
            result.onSelect = ({ item }) => {
              $("#search-bar-collapse").collapse('hide');
              setInstantSearchUiState({ query: item.query }, true);
            };

            return result;
          },
        };
      },
    });

    const queryParams = new URLSearchParams(window.location.search);
    const self = this;

    this.autocompleteInstance = autocomplete({
      container: this.$refs.autocompleteContainer,
      panelContainer: '#logo-bar',
      panelPlacement: 'full-width',
      placeholder: 'Wonach suchst du?',
      translations: {
        placeholder: 'Wonach suchst du?',
        noResults: 'Keine Ergebnisse gefunden',
        clearButtonTitle: 'Suche zurücksetzen',
        detachedCancelButtonText: 'Abbrechen',
        detachedSearchButtonTitle: 'Suchen',
        submitButtonTitle: 'Suchen',
      },
      classNames: {
        submitButton: 'btn btn-secondary kk-icon kk-icon-search rounded-right',
        form: 'rounded',
      },
      detachedMediaQuery: 'none',
      initialState: { query: queryParams.get('query') || '' },
      openOnFocus: true,
      plugins: [recentSearchesPlugin, querySuggestionsPlugin],
      async getSources ({ query }) {
        const typesenseFacets = await typesenseClient.collections(INSTANT_SEARCH_INDEX_NAME).documents().search({
          q: query || '*',
          preset: INSTANT_SEARCH_PRESET,
          facet_by: ['category.lvl1', 'category.lvl2', 'system_manufacturer'],
          max_facet_values: 5,
          limit: 1
        });

        return [
          {
            sourceId: 'facetTagSystem',
            templates: {
              header ({ html }) {
                return html`<div>
                  <span class="aa-SourceHeaderTitle">Kategorie</span>
                  <div class="aa-SourceHeaderLine"></div>
                </div>`;
              },
              item ({ item, html, createElement }) {
                const path = item.value.split(' > ');
                const categoryQuery = path.map((value, i) => encodeURIComponent(`hierarchicalMenu[category.lvl1][${i}]`) + '=' + encodeURIComponent(value)).join('&');
                return window.instantSearchInstance
                  ? createElement('a', {
                    onclick: () => {
                      $("#search-bar-collapse").collapse('hide');
                      setInstantSearchUiState({ query, hierarchicalMenu: { 'category.lvl1': path } }, true);
                    }
                  }, item.value)
                  : html`<a href="/suche/?query=${query}&${categoryQuery}">${item.value}</a>`
              },
              noResults ({ html }) {
                return html`<img src="${konsolenkostPluginInstantSearchPath}/images/icons/no-result.svg" style="width: auto; height: 1em;" />`;
              },
            },
            getItems () {
              const [lvl1, lvl2] = typesenseFacets.facet_counts

              return [...lvl1.counts, ...lvl2.counts]
                .sort((a, b) => b.count - a.count)
                .slice(0, 5)
                .sort((a, b) => a.value.localeCompare(b.value));
            },
          },
          {
            sourceId: 'facetTagSystemManufacturer',
            templates: {
              header ({ html }) {
                return html`<div>
                  <span class="aa-SourceHeaderTitle">Marken</span>
                  <div class="aa-SourceHeaderLine"></div>
                </div>`;
              },
              item ({ item, html, createElement }) {
                const { icon, color = "#000000", textColor = "#FFFFFF", keepText } = manufacturerIcons[item.value] ?? {};
                const style = `
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  justify-content: center;
                  height: 1.75rem;
                  padding: .3rem 0.5rem;
                  gap: .5rem;
                  color: ${textColor};
                  background-color: ${color};
                `;

                return window.instantSearchInstance
                  ? createElement('a', {
                    onclick: () => setInstantSearchUiState({
                      query,
                      refinementList: { system_manufacturer: [item.value] }
                    }, true),
                    style: style,
                    title: item.value
                  }, [
                    createElement('img', {
                      src: icon,
                      style: 'object-fit: contain; width: 100%; height: 100%'
                    }),
                    keepText ? item.value : ''
                  ])
                  : html`<a href="/suche/?query=${query}&refinementList[system_manufacturer][0]=${encodeURIComponent(item.value)}" style="${style}" title="${item.value}" >
                  <img src="${icon}" style="object-fit: contain; width: 100%; height: 100%" />
                    ${keepText ? item.value : ''}
                  </a>`;
              },
              noResults ({ html }) {
                return html`<img src="${konsolenkostPluginInstantSearchPath}/images/icons/no-result.svg" style="width: auto; height: 1em;" />`;
              },
            },
            getItems () {
              return typesenseFacets.facet_counts[2].counts
            },
          },
          {
            sourceId: 'products',
            templates: {
              header ({ html }) {
                return html`<div>
                  <span class="aa-SourceHeaderTitle">Produkte</span>
                  <div class="aa-SourceHeaderLine"></div>
                </div>`;
              },
              item ({ item, html, components }) {
                return html`<a href="${item.url}" style="display: flex; gap: 1rem; align-items: center; ${item.availability === 'Ausverkauft' ? 'opacity: 0.5; background-color: #f5f5f5;' : ''}">
                  <img src="${item.cover_thumbnail}" width="72px" height="72px" style="object-fit: contain;" />
                  <div style="flex-grow: 1;">
                    <b style="display: block; margin-bottom: 0.5rem">
                      ${components.Highlight({ hit: item, attribute: 'name', tagName: 'em' })}
                    </b>
                    <div>
                      ${item.availability}
                    </div>
                    <div class="text-primary font-weight-bold">
                      ab ${item.min_price.toFixed(2).replace('.', ',')} €
                    </div>
                  </div>
                </a>`;
              },
              noResults ({ html }) {
                return html`<img src="${konsolenkostPluginInstantSearchPath}/images/icons/no-result.svg" style="width: auto; height: 1em;" /> Keine Ergebnisse gefunden`;
              },
            },
            getItems () {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: INSTANT_SEARCH_INDEX_NAME,
                    params: { query, hitsPerPage: 5 },
                  },
                ],
              });
            },
          },
          {
            sourceId: 'more',
            templates: {
                item ({ item, createElement }) {
                const style = 'display: flex; justify-content: center; align-items: center; height: 40px;';
                const onclick = () => $('#search-bar-collapse').collapse('hide');

                return window.instantSearchInstance
                  ? createElement('button', { style, onclick }, item.value)
                  : createElement('a', { href: `/suche/?query=${query}`, style, onclick }, item.value);
                }
            },
            getItems () {
              return [
                { value: "Mehr..." }
              ];
            }
          }
        ]
      },
      render ({ elements, render, html }, root) {
        const {
          recentSearchesPlugin,
          querySuggestionsPlugin,
          facetTagSystemManufacturer,
          facetTagSystem,
          products,
          more,
        } = elements;

        render(
          html`<div class="aa-PanelLayout aa-Panel--scrollable">
                  <div class="aa-PanelSections">
                    <div class="aa-PanelSection--left">
                      ${querySuggestionsPlugin}
                      ${recentSearchesPlugin}
                      ${facetTagSystem}
                      ${facetTagSystemManufacturer}
                    </div>
                    <div class="aa-PanelSection--right">
                      ${products}
                      ${more}
                    </div>
                  </div>
          </div>`,
          root
        );
      },
      onSubmit ({ state }) {
        $("#search-bar-collapse").collapse('hide');
        setInstantSearchUiState({ query: state.query }, true);
      },
      onReset () {
        setInstantSearchUiState({
          query: '',
          refinementList: {},
          numericMenu: {},
          ratingMenu: {},
          range: {},
          toggle: {},
          geoSearch: {}
        });
      },
      onStateChange ({ prevState, state }) {
        if (prevState.query !== state.query) {
          debouncedInstantSearchUiState({ query: state.query });
        }
      },
    });

    const checkForInstantSearch = () => {
      if (window.instantSearchInstance) {
        this.registerInstantSearchInstance(window.instantSearchInstance);
      } else {
        setTimeout(checkForInstantSearch, 100);
      }
    }
    checkForInstantSearch();
  },
  beforeDestroy () {
    this.autocompleteInstance?.destroy();
  },
});
